import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Star, CheckCircle } from 'lucide-react';
import productexLogo from '../components/layout/productex-logo.png';

interface SurveyData {
  id: string;
  token: string;
  entity_name: string;
  entity_type: string;
  salesperson: string;
  client_name: string | null;
  client_email: string | null;
  status: string;
}

const SatisfactionSurvey: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [survey, setSurvey] = useState<SurveyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [q1, setQ1] = useState<number | null>(null);
  const [q2, setQ2] = useState<number | null>(null);
  const [q3, setQ3] = useState<number | null>(null);
  const [q4, setQ4] = useState<number | null>(null);
  const [q5, setQ5] = useState<boolean | null>(null);
  const [q6, setQ6] = useState<boolean | null>(null);
  const [q6Target, setQ6Target] = useState('');
  const [q7, setQ7] = useState('');

  useEffect(() => {
    if (token) loadSurvey();
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('satisfaction_surveys')
        .select('id, token, entity_name, entity_type, salesperson, client_name, client_email, status')
        .eq('token', token)
        .maybeSingle();

      if (err) throw err;
      if (!data) {
        setError('Encuesta no encontrada');
        setLoading(false);
        return;
      }
      if (data.status === 'completed') {
        setSubmitted(true);
      }
      setSurvey(data as SurveyData);
    } catch (e: any) {
      setError(e.message || 'Error al cargar la encuesta');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || q1 === null || q2 === null || q3 === null || q4 === null || q5 === null || q6 === null) return;
    setSubmitting(true);
    try {
      const { data, error: err } = await supabase.rpc('submit_satisfaction_survey', {
        p_token: token,
        p_q1: q1, p_q2: q2, p_q3: q3, p_q4: q4,
        p_q5: q5, p_q6: q6,
        p_q6_target: q6 ? q6Target : null,
        p_q7: q7 || null,
      });
      const result = data as { success?: boolean; error?: string };
      if (err || !result?.success) {
        throw new Error(result?.error || err?.message || 'Error al enviar');
      }
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message || 'Error al enviar la encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  const RatingStars = ({ value, onChange }: { value: number | null; onChange: (v: number) => void }) => (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-5">1</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-1 rounded-lg transition-colors hover:scale-110 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <Star
              className={`h-10 w-10 ${
                value !== null && n <= value
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-gray-300 hover:text-amber-300'
              }`}
            />
          </button>
        ))}
      </div>
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-5">5</span>
    </div>
  );

  const YesNoButtons = ({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) => (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-6 py-3 rounded-xl font-medium transition-all ${
          value === true
            ? 'bg-green-500 text-white shadow-lg'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/30'
        }`}
      >
        Sí
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-6 py-3 rounded-xl font-medium transition-all ${
          value === false
            ? 'bg-red-500 text-white shadow-lg'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30'
        }`}
      >
        No
      </button>
    </div>
  );

  const PageShell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
      <header className="bg-black text-white px-6 py-4 flex items-center justify-center">
        <img
          src={productexLogo}
          alt="Productex"
          className="h-8 w-auto object-contain"
        />
      </header>
      {children}
    </div>
  );

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-500 border-t-transparent mx-auto" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando encuesta...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (error && !survey) {
    return (
      <PageShell>
        <div className="flex items-center justify-center p-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
            <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">El link puede haber expirado o ser incorrecto.</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <div className="flex items-center justify-center p-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">¡Gracias!</h1>
            <p className="text-gray-600 dark:text-gray-400">Tu opinión es muy importante para nosotros.</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!survey) return null;

  const canSubmit = q1 !== null && q2 !== null && q3 !== null && q4 !== null && q5 !== null && q6 !== null;

  return (
    <PageShell>
      <div className="py-12 px-4">
        <div className="max-w-xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
            {/* Info producción/cliente */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm space-y-1">
                <p>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {survey.entity_type === 'production' ? 'Producción' : 'Muestra'}:
                  </span>{' '}
                  <span className="text-gray-900 dark:text-white">{survey.entity_name}</span>
                </p>
                {survey.client_name && (
                  <p>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Cliente:</span>{' '}
                    <span className="text-gray-900 dark:text-white">{survey.client_name}</span>
                  </p>
                )}
                {survey.client_email && (
                  <p>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Email:</span>{' '}
                    <span className="text-gray-900 dark:text-white">{survey.client_email}</span>
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-6 text-center border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Encuesta de satisfacción</h1>
            </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Q1 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Cómo calificarías tu experiencia con Productex, en términos generales?
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">1 = Muy mala · 5 = Excelente</p>
              <RatingStars value={q1} onChange={setQ1} />
            </div>

            {/* Q2 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Cómo calificarías la calidad del producto entregado? (Tela, costuras, estampados, bordados, avíos, etc.)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">1 = Muy mala · 5 = Excelente</p>
              <RatingStars value={q2} onChange={setQ2} />
            </div>

            {/* Q3 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Cómo calificarías el cumplimiento de tiempos de entrega?
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">1 = Muy mala · 5 = Excelente</p>
              <RatingStars value={q3} onChange={setQ3} />
            </div>

            {/* Q4 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Cómo calificarías la comunicación por parte de {survey.salesperson}, tu asesor de ventas?
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">1 = Muy mala · 5 = Excelente</p>
              <RatingStars value={q4} onChange={setQ4} />
            </div>

            {/* Q5 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Volverías a producir con Productex?
              </label>
              <YesNoButtons value={q5} onChange={setQ5} />
            </div>

            {/* Q6 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                ¿Recomendarías a Productex a conocidos, amigos o familiares?
              </label>
              <YesNoButtons value={q6} onChange={setQ6} />
              {q6 === true && (
                <div className="mt-4">
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    ¿A qué marca o potencial cliente nos recomendarías?
                  </label>
                  <input
                    type="text"
                    value={q6Target}
                    onChange={(e) => setQ6Target(e.target.value)}
                    placeholder="Opcional"
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                  />
                </div>
              )}
            </div>

            {/* Q7 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                ¿Hay algún comentario adicional que te gustaría dejarnos?
              </label>
              <textarea
                value={q7}
                onChange={(e) => setQ7(e.target.value)}
                rows={4}
                placeholder="Opcional"
                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full py-4 rounded-xl bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors"
              >
                {submitting ? 'Enviando...' : 'Enviar encuesta'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </PageShell>
  );
};

export default SatisfactionSurvey;
